import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DigitalSignature = ({ onSignature, clientName, isLoading }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureName, setSignatureName] = useState(clientName || '');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (canvas) {
      const ctx = canvas?.getContext('2d');
      ctx.strokeStyle = '#1E3A8A';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, []);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const ctx = canvas?.getContext('2d');
    
    const x = (e?.clientX || e?.touches?.[0]?.clientX) - rect?.left;
    const y = (e?.clientY || e?.touches?.[0]?.clientY) - rect?.top;
    
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef?.current;
    const rect = canvas?.getBoundingClientRect();
    const ctx = canvas?.getContext('2d');
    
    const x = (e?.clientX || e?.touches?.[0]?.clientX) - rect?.left;
    const y = (e?.clientY || e?.touches?.[0]?.clientY) - rect?.top;
    
    ctx?.lineTo(x, y);
    ctx?.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');
    ctx?.clearRect(0, 0, canvas?.width, canvas?.height);
    setHasSignature(false);
  };

  const handleSubmitSignature = () => {
    if (!signatureName?.trim()) {
      setNameError('Veuillez saisir votre nom complet');
      return;
    }

    if (!hasSignature) {
      return;
    }

    const canvas = canvasRef?.current;
    const signatureData = canvas?.toDataURL();
    
    onSignature({
      signature: signatureData,
      name: signatureName?.trim(),
      timestamp: new Date()?.toISOString()
    });
  };

  const handleNameChange = (e) => {
    setSignatureName(e?.target?.value);
    if (nameError) setNameError('');
  };

  if (isLoading) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
          <div className="h-32 bg-muted rounded mb-4"></div>
          <div className="h-10 bg-muted rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
      <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center space-x-2">
        <Icon name="PenTool" size={20} />
        <span>Signature Numérique</span>
      </h2>
      <div className="space-y-4">
        <Input
          label="Nom complet du signataire"
          type="text"
          placeholder="Saisissez votre nom complet"
          value={signatureName}
          onChange={handleNameChange}
          error={nameError}
          required
        />

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Signature <span className="text-error">*</span>
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="w-full h-32 bg-white border border-border rounded cursor-crosshair touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-text-secondary">
                Signez dans la zone ci-dessus avec votre souris ou votre doigt
              </p>
              <Button
                variant="ghost"
                size="sm"
                iconName="RotateCcw"
                onClick={clearSignature}
                disabled={!hasSignature}
              >
                Effacer
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 text-xs text-text-secondary">
          <Icon name="Shield" size={14} />
          <span>
            Votre signature numérique est sécurisée et horodatée. 
            Elle a la même valeur légale qu'une signature manuscrite.
          </span>
        </div>

        <Button
          variant="success"
          size="lg"
          iconName="Check"
          iconPosition="left"
          onClick={handleSubmitSignature}
          disabled={!hasSignature || !signatureName?.trim()}
          fullWidth
          className="mt-6"
        >
          Confirmer la Signature
        </Button>
      </div>
    </div>
  );
};

export default DigitalSignature;